import React, {useState, useEffect, createContext, useContext, useCallback} from 'react';
import {ClerkProvider, useAuth, SignIn as ClerkSignIn, SignInButton as ClerkSignInButton, UserButton as ClerkUserButton} from '@clerk/clerk-react';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {SubscribeDevClient} from '@subscribe.dev/client';

type SyncStatus = 'local' | 'syncing' | 'synced' | 'error';

// Wrapper for SignIn that enforces iframe-compatible settings
export interface SignInProps {
    [key: string]: any;
}

export function SignIn(props: SignInProps) {
    return (
        <ClerkSignIn
            {...props}
            forceRedirectUrl={window.location.href}
            oauthFlow="popup"
        />
    );
}

// Wrapper for SignInButton that enforces iframe-compatible settings
export interface SignInButtonProps {
    [key: string]: any;
}

export function SignInButton(props: SignInButtonProps) {
    return (
        <ClerkSignInButton
            {...props}
            forceRedirectUrl={window.location.href}
            oauthFlow="popup"
            mode={"modal"}
        />
    );
}

// Wrapper for UserButton that enforces iframe-compatible settings
export interface UserButtonProps {
    [key: string]: any;
}

export function UserButton(props: UserButtonProps) {
    return (
        <ClerkUserButton
            {...props}
            afterSignOutUrl={window.location.href}
        />
    );
}

export function useClerkAuth() {
    const [token, setToken] = useState<string | null>(null);
    const { isSignedIn, getToken } = useAuth();
    
    useEffect(() => {
        if (isSignedIn) {
            // Always get fresh token, don't cache
            getToken({ skipCache: true, template: "default" }).then(freshToken => {
                setToken(freshToken || null);
            }).catch(err => {
                console.error('Failed to get fresh token:', err);
                setToken(null);
            });
        } else {
            setToken(null);
        }
    }, [getToken, isSignedIn]);

    return { token, isSignedIn }
}


interface SubscribeDevContextValue {
    client: SubscribeDevClient | null;
    token: string | null;
    isSignedIn: boolean;
    queryClient: QueryClient;
    subscriptionModalUrl: string | null;
    setSubscriptionModalUrl: (url: string | null) => void;
    projectToken: string;
    effectiveApiKey: string;
    showSignIn: boolean;
    setShowSignIn: (show: boolean) => void;
}

const SubscribeDevContext = createContext<SubscribeDevContextValue | null>(null);

export function useSubscribeDevProvider() {
    const context = useContext(SubscribeDevContext);
    if (!context) {
        throw new Error('useSubscribeDevProvider must be used within SubscribeDevProvider');
    }
    return context;
}

function useSubscribeDevSessionStorage<T>(key: string, initialValue: T): [T, (value: T) => void, SyncStatus] {
    const {client} = useSubscribeDevProvider();
    const storage = useStorage();
    const [currentData, setCurrentData] = useState<T>(initialValue);
    const [syncStatus, setSyncStatus] = useState<SyncStatus>('local');
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const initializeData = async () => {
            console.log(`Initializing storage for key: ${key}, client available: ${!!client}`);

            if (!client) {
                console.log(`Client not ready yet for key: ${key}`);
                return;
            }

            setSyncStatus('syncing');
            try {
                const storedValue = await storage.get(key);
                console.log(`Retrieved stored value for ${key}:`, storedValue);
                if (storedValue) {
                    try {
                        console.log("STORED VALUE: ", storedValue)
                        const parsed = JSON.parse(storedValue);
                        console.log("PARSED DATA: ", parsed)

                        // Handle case where data was stored as {key: value} structure
                        let dataToSet = parsed;
                        if (parsed && typeof parsed === 'object' && parsed[key] !== undefined) {
                            dataToSet = parsed[key];
                            console.log("EXTRACTED VALUE FOR KEY: ", key, dataToSet);
                        }

                        // Validate that parsed data is not corrupted
                        if (dataToSet === null || dataToSet === undefined || (typeof dataToSet === 'string' && dataToSet.includes('[object Object]'))) {
                            throw new Error('Corrupted storage data detected');
                        }
                        setCurrentData(dataToSet);
                    } catch (parseError) {
                        console.warn(`Corrupted storage data for key ${key}, overwriting with initial value:`, parseError);
                        setCurrentData(initialValue);
                        console.log("SETTING TO: ", initialValue)
                        // Overwrite the bad storage with good initial value
                        await storage.set(key, JSON.stringify(initialValue));
                        console.log(`Overwrote corrupted storage for key ${key} with initial value`);
                    }
                } else {
                    setCurrentData(initialValue);
                }
                setSyncStatus('synced');
            } catch (error) {
                console.error('Failed to initialize data:', error);
                setCurrentData(initialValue);
                setSyncStatus('error');
            }
            setIsInitialized(true);
        };

        if (!isInitialized) {
            initializeData();
        }
    }, [key, initialValue, client, isInitialized, storage]);

    const setValue = useCallback(async (value: T) => {
        setCurrentData(value);

        if (!client) {
            console.warn(`setValue: No client available for key ${key}`);
            return;
        }

        setSyncStatus('syncing');

        try {
            await storage.set(key, JSON.stringify(value));
            setSyncStatus('synced');
        } catch (error) {
            console.error('Failed to save data:', error);
            setSyncStatus('error');
        }
    }, [key, client, storage]);

    return [currentData, setValue, syncStatus];
}

export function useSubscribeDev() {
    const {client, token, isSignedIn, setShowSignIn} = useSubscribeDevProvider();
    const {subscribe, isSubscribed, subscriptionStatus, subscriptionLoading, refreshSubscriptionStatus} = useSubscribeDevAuth();
    const usage = useUsage();

    const signIn = useCallback(() => {
        setShowSignIn(true);
    }, [setShowSignIn]);

    return {
        client,
        token,
        isSignedIn,
        signIn,
        subscribe,
        isSubscribed,
        subscriptionStatus,
        subscriptionLoading,
        refreshSubscriptionStatus,
        useStorage: useSubscribeDevSessionStorage,
        usage
    };
}


function useSubscribeDevAuth() {
    const {isSignedIn, token, client} = useSubscribeDevProvider();
    const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    const refreshSubscriptionStatus = useCallback(async () => {
        if (client && isSignedIn) {
            setLoading(true);
            try {
                const status = await client.getSubscriptionStatus();
                setSubscriptionStatus(status);
            } catch (error) {
                console.error('Failed to refresh subscription status:', error);
            } finally {
                setLoading(false);
            }
        } else {
            setSubscriptionStatus(null);
        }
    }, [client, isSignedIn]);

    useEffect(() => {
        refreshSubscriptionStatus();
    }, [refreshSubscriptionStatus]);

    // Listen for refresh events from subscription modal closure
    useEffect(() => {
        const handleRefresh = () => {
            refreshSubscriptionStatus();
        };

        window.addEventListener('subscribedev:refresh-subscription', handleRefresh);
        return () => window.removeEventListener('subscribedev:refresh-subscription', handleRefresh);
    }, [refreshSubscriptionStatus]);

    const isSubscribed = subscriptionStatus?.hasActiveSubscription || false;

    const { setSubscriptionModalUrl, effectiveApiKey, projectToken } = useSubscribeDevProvider();

    const subscribe = useCallback(() => {
        if (!client?.pricingUrl || !token) return;
        
        // If no project token was provided and we don't have a demo key yet, wait
        if (!projectToken && !effectiveApiKey) {
            console.warn('No project token or demo key available yet, cannot open subscription modal');
            return;
        }

        const url = new URL(client.pricingUrl);
        if (token) {
            url.searchParams.set('user_token', token);
        }
        if (effectiveApiKey) {
            url.searchParams.set('project_token', effectiveApiKey);
        }

        setSubscriptionModalUrl(url.toString());
    }, [client, token, setSubscriptionModalUrl, effectiveApiKey, projectToken]);

    return {
        isSignedIn,
        token,
        isSubscribed,
        subscribe,
        subscriptionStatus,
        subscriptionLoading: loading,
        refreshSubscriptionStatus
    };
}

function useUsage() {
    const { client, isSignedIn } = useSubscribeDevProvider();
    const [allocatedCredits, setAllocatedCredits] = useState<number>(0);
    const [usedCredits, setUsedCredits] = useState<number>(0);
    const [remainingCredits, setRemainingCredits] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const refreshUsage = useCallback(async () => {
        if (!client || !isSignedIn) {
            setAllocatedCredits(0);
            setUsedCredits(0);
            setRemainingCredits(0);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            const usageData = await client.getUsage();
            const userBalance = usageData.credits.userBalance;
            setAllocatedCredits(userBalance?.allocatedCredits || 0);
            setUsedCredits(userBalance?.usedCredits || 0);
            setRemainingCredits(userBalance?.remainingCredits || 0);
        } catch (err: any) {
            console.warn('Failed to fetch balance data:', err);
            if (err.name === 'ValidationError' && err.message.includes('User key is required')) {
                setError('User authentication required to fetch balance data');
            } else {
                setError(err.message || 'Failed to fetch balance data');
            }
            setAllocatedCredits(0);
            setUsedCredits(0);
            setRemainingCredits(0);
        } finally {
            setLoading(false);
        }
    }, [client, isSignedIn]);

    // Initial load
    useEffect(() => {
        refreshUsage();
    }, [refreshUsage]);

    // Listen for usage-changed events from client
    useEffect(() => {
        if (!client) return;

        const handleUsageChange = () => {
            console.log('Usage changed, refreshing...');
            refreshUsage();
        };

        client.on('usage-changed', handleUsageChange);
        
        return () => {
            client.off('usage-changed', handleUsageChange);
        };
    }, [client, refreshUsage]);

    return {
        allocatedCredits,
        usedCredits,
        remainingCredits,
        loading,
        error,
        refreshUsage
    };
}

function useStorage() {
    const {client} = useSubscribeDevProvider();

    const get = async (key: string) => {
        if (!client) {
            console.warn(`useStorage.get: No client available for key ${key}`);
            return null;
        }

        try {
            console.log(`Getting remote storage for key ${key} to check for updates`);

            // Get the full storage object from the API
            const storageResponse = await client.getStorage();
            const localData = localStorage.getItem(key);

            if (storageResponse?.sessionData?.[key] !== undefined) {
                console.log(`Successfully retrieved remote storage for key ${key}`);
                const remoteValue = storageResponse.sessionData[key];
                // Remote data always overwrites local - aggressive sync
                localStorage.setItem(key, JSON.stringify(remoteValue));
                return JSON.stringify(remoteValue);
            } else {
                // No remote data available - if we have local data but no remote, purge local
                if (localData) {
                    console.log(`No remote data for key ${key}, purging local storage`);
                    localStorage.removeItem(key);
                }
                console.log(`No data found for key ${key}`);
                return null;
            }
        } catch (error) {
            console.warn(`Failed to get storage for key ${key}:`, error);
            // On any backend error, aggressively purge local storage
            console.log(`Backend error for key ${key}, purging local storage`);
            localStorage.removeItem(key);
            return null;
        }
    };

    const set = async (key: string, value: string) => {
        if (!client) {
            console.warn(`useStorage.set: No client available for key ${key}`);
            return;
        }

        try {
            // Parse the value to store in sessionData
            let parsedValue;
            try {
                parsedValue = JSON.parse(value);
            } catch {
                parsedValue = value;
            }

            // Get current storage to preserve other keys
            let currentSessionData: Record<string, any> = {};
            try {
                const currentStorage = await client.getStorage();
                currentSessionData = currentStorage?.sessionData || {};
            } catch (error) {
                console.warn('Could not get current storage, using empty object:', error);
                // On error getting current storage, purge local storage
                localStorage.removeItem(key);
                throw error;
            }

            // Update the specific key in sessionData
            const updatedSessionData = {
                ...currentSessionData,
                [key]: parsedValue
            };

            await client.setStorage(updatedSessionData);
            console.log(`Successfully set remote storage for key ${key}`);
            
            // Only set local storage after successful remote storage
            localStorage.setItem(key, value);
        } catch (error) {
            console.warn(`Failed to set storage for key ${key}:`, error);
            // On any backend error, aggressively purge local storage
            console.log(`Backend error setting key ${key}, purging local storage`);
            localStorage.removeItem(key);
            throw error;
        }
    };

    const remove = async (key: string) => {
        if (!client) return;

        try {
            // Get current storage to preserve other keys
            let currentSessionData: Record<string, any> = {};
            try {
                const currentStorage = await client.getStorage();
                currentSessionData = currentStorage?.sessionData || {};
            } catch (error) {
                console.warn('Could not get current storage for removal:', error);
                // Always remove from local storage even if remote fails
                localStorage.removeItem(key);
                return;
            }

            // Remove the specific key from sessionData
            const updatedSessionData = { ...currentSessionData };
            delete updatedSessionData[key];

            await client.setStorage(updatedSessionData);
            console.log(`Successfully removed remote storage for key ${key}`);
            
            // Only remove from local storage after successful remote removal
            localStorage.removeItem(key);
        } catch (error) {
            console.warn(`Failed to remove storage for key ${key}:`, error);
            // On backend error, still remove from local storage to stay consistent
            console.log(`Backend error removing key ${key}, removing from local storage anyway`);
            localStorage.removeItem(key);
        }
    };

    return {get, set, remove};
}

interface SubscriptionModalProps {
    url: string | null;
    onClose: () => void;
    onSubscriptionChange?: () => void;
}

function SubscriptionModal({ url, onClose, onSubscriptionChange }: SubscriptionModalProps) {
    useEffect(() => {
        if (!url) return;

        const handleMessage = (event: MessageEvent) => {
            // Listen for subscription success message from iframe
            if (event.data?.type === 'REMETRICATE_SUBSCRIPTION_SUCCESS' &&
                event.data?.action === 'CLOSE_MODAL') {
                console.log('Subscription completed successfully:', event.data);
                onSubscriptionChange?.();
                onClose();
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [url, onClose, onSubscriptionChange]);

    if (!url) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    width: '90vw',
                    height: '90vh',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    position: 'relative',
                    overflow: 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        zIndex: 10001,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '30px',
                        height: '30px',
                        cursor: 'pointer',
                        fontSize: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    ×
                </button>
                <iframe
                    src={url}
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        overflow: 'hidden'
                    }}
                    title="Subscription"
                />
            </div>
        </div>
    );
}

interface SubscribeDevProviderProps {
    children: React.ReactNode;
    projectToken?: string;
    clerkPublishableKey?: string;
    baseUrl?: string
}

export function SubscribeDevProvider({
                                         children,
                                         projectToken,
                                         clerkPublishableKey = "pk_test_Y2hhbXBpb24tYmx1ZWdpbGwtMjIuY2xlcmsuYWNjb3VudHMuZGV2JA",
                                         baseUrl
}: SubscribeDevProviderProps) {
    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 1000 * 60 * 5, // 5 minutes
                refetchOnWindowFocus: false,
            },
        },
    });

    return (
        <ClerkProvider publishableKey={clerkPublishableKey} telemetry={false}>
            <QueryClientProvider client={queryClient}>
                <SubscribeDevProviderInner projectToken={projectToken} queryClient={queryClient} baseUrl={baseUrl}>
                    {children}
                </SubscribeDevProviderInner>
            </QueryClientProvider>
        </ClerkProvider>
    );
}

function SubscribeDevProviderInner({children, projectToken, queryClient, baseUrl}: {
    children: React.ReactNode;
    projectToken?: string;
    queryClient: QueryClient,
    baseUrl?: string
}) {
    const {token, isSignedIn} = useClerkAuth();
    const [client, setClient] = useState<SubscribeDevClient | null>(null);
    const [subscriptionModalUrl, setSubscriptionModalUrl] = useState<string | null>(null);
    const [demoApiKey, setDemoApiKey] = useState<string | null>(null);
    const [showSignIn, setShowSignIn] = useState<boolean>(false);

    useEffect(() => {
        if (isSignedIn && token) {
            // Use demo API key if we have one, otherwise use projectToken
            const effectiveApiKey = demoApiKey || projectToken;
            
            // Only include baseUrl in the config if it's provided
            const config: any = {
                // baseUrl: import.meta.env.VITE_API_URL,
                apiKey: effectiveApiKey,
                userKey: token
            };

            if (baseUrl) {
                config.baseUrl = baseUrl;
            }

            const newClient = new SubscribeDevClient(config);

            // Only fetch demo project if we don't have a projectToken AND don't already have a demo key
            if (!projectToken && !demoApiKey) {
                console.warn('No project token provided, using default project token');
                newClient.getDemoProject().then(demoProject=>{
                    if (!demoProject?.apiKey) {
                        throw new Error("No project token created and cannot fetch demo token.")
                    }
                    // Store the demo API key in state for future re-renders
                    setDemoApiKey(demoProject.apiKey);
                    
                    // Create new client with the demo API key
                    const demoConfig = {
                        ...config,
                        apiKey: demoProject.apiKey
                    };
                    const demoClient = new SubscribeDevClient(demoConfig);
                    setClient(demoClient);
                }).catch(console.error)
            } else {
                setClient(newClient);
            }

        } else {
            setClient(null);
        }
    }, [isSignedIn, token, projectToken, baseUrl, demoApiKey]);

    const handleModalClose = () => {
        setSubscriptionModalUrl(null);
        // Trigger a refresh of subscription status across the app
        window.dispatchEvent(new CustomEvent('subscribedev:refresh-subscription'));
    };

    const handleSignInClose = () => {
        setShowSignIn(false);
    };

    // Auto-hide SignIn modal when user signs in
    useEffect(() => {
        if (isSignedIn) {
            setShowSignIn(false);
        }
    }, [isSignedIn]);

    // Calculate effective API key for context
    const effectiveApiKey = demoApiKey || projectToken;
    
    const contextValue: SubscribeDevContextValue = {
        client,
        token,
        isSignedIn: !!isSignedIn,
        queryClient,
        subscriptionModalUrl,
        setSubscriptionModalUrl,
        projectToken,
        effectiveApiKey,
        showSignIn,
        setShowSignIn
    };

    return (
        <SubscribeDevContext.Provider value={contextValue}>
            {children}
            <SubscriptionModal
                url={subscriptionModalUrl}
                onClose={handleModalClose}
                onSubscriptionChange={() => window.dispatchEvent(new CustomEvent('subscribedev:refresh-subscription'))}
            />
            {showSignIn && !isSignedIn && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        zIndex: 10000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }}
                    onClick={handleSignInClose}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                    >
                        <SignIn />
                    </div>
                </div>
            )}
        </SubscribeDevContext.Provider>
    );
}